import React from 'react';
import { useAuthState } from '@/utils/auth';
import { RbacStateManager, __INHERITS__, RbacPermissions } from './useRbac';
import { RoleNames } from '@/generated/codegen';
import { ReportType } from '@/modules/reports/ReportTypes';
import { BILDERIT_MACHINE_GROUP } from '@/modules/super_root/superRootSchema';
import { isMobileApp } from '@/utils/isMobileApp';

export const BilderitRbacManager: React.FC<{}> = () => {
  const { user } = useAuthState();
  return <RbacStateManager context={user} permissions={permissions} />;
};

const permissions: Required<RbacPermissions> = {
  // permissions valid for all roles
  '*': {
    [`create-report:${ReportType.DailyReport}`]: true,
    'sidebar:Bilderit Handlers': true,
  },
  [RoleNames.RentalApprover]: {
    'rental-certification:review': true,
  },
  [RoleNames.RentalExternalApprover]: {
    'rental-certification:review': true,
  },
  [RoleNames.RentalIssuer]: {
    'rental-certification:create': true,
    'rental_params:project:edit': true,
  },
  [RoleNames.Btrack]: {
    'btrack:open': true,
  },
  [RoleNames.Machinist]: {
    'app:operario': true,
    'view-machinist-dashboard-statistics:hours': true,
    [`create-report:${ReportType.DailyReport}`]: true,
    [`create-report:${ReportType.MachineNotice}`]: true,
    [`create-report:${ReportType.Checklist}`]: true,
  },
  [RoleNames.FuelDispatcher]: {
    [`create-report:${ReportType.FuelReport}` as const]: true,
    [`create-report:${ReportType.StockReport}` as const]: true,
    'view-machinist-dashboard-statistics:dispatches': true,
    [__INHERITS__]: [RoleNames.Machinist],
  },
  [RoleNames.Mechanic]: {
    'app:operario': true,
    'work-order:execute': true,
    'work-order:view': true,
    'work-order:view-assigned': true,
    [`create-report:${ReportType.MachineNotice}`]: true,
    [`create-report:${ReportType.Checklist}`]: true,
    'view:machine-history': true,
    'view-machinist-dashboard-statistics:wos_assigned': true,
    'view-machinist-dashboard-statistics:wos_completed': true,
  },
  [RoleNames.Admin]: {
    'sidebar:Modules': true,
    'sidebar:Bilderit Handlers': true,
    'sidebar:Company Handlers': true,
    'sidebar:Compañía': true,
    'sidebar:Compañía:Usuarios': ({ args }) =>
      import.meta.env.VITE_APP_BRANCH !== 'master' ||
      args?.filterQuery?.company_id !== 8,
    'sidebar:Equipos': true,
    'sidebar:Seguimiento': true,
    'sidebar:Partes Diarios': true,
    'sidebar:Combustible': true,
    'sidebar:Gestión de Taller': true,
    'sidebar:Rental': true,
    'reports:settings': true,
    'maintenance_plan:view': true,
    'checklist:results:view': true,
    'checklist:template:view': true,
    'consumable:view': true,
    'work_order:add': () => !isMobileApp(),
    'work_order:edit': true,
    'work_order:cancel': true,
    'work_order:tag:in_progress:edit': true,
    'work_order:change_project': true,
    'wo_hours:delete_executed': true,
    'wo_hours:edit_executed': true,
    'wo_hours:edit_execution_date': true,
    'service_report:remove': true,
    'assignment_report:remove': true,
    'services:config': true,
    'app:manager': true,
    'machinist:remove': true,
    'mechanic:remove': true,
    'maintenance_manager:remove': true,
    'machinist:add': true,
    'maintenance:add': true,
    'mechanic:add': true,
    'btrack:add': true,
    'btrack:remove': true,
    'employee:add': true,
    'fuel_dispatcher:add': true,
    'fuel_dispatcher:remove': true,
    'fuel_tanks:assign': true,
    'fuel_tanks:unassign': true,
    'fuel_consumers:assign': true,
    'fuel_consumers:unassign': true,
    [__INHERITS__]: [
      RoleNames.FuelDispatcher,
      RoleNames.Mechanic,
      RoleNames.Machinist,
    ],
  },
  [RoleNames.Manager]: {
    'rental-certification:manage-copy-recipients': true,
    'fuel_cost:edit': true,
    'sidebar:B-Connect': true,
    'maintenance_plan:add': true,
    'maintenance_plan:edit': true,
    'checklist:template:edit': true,
    'work_order:remove': true,
    'wo_hours:edit_planned': true,
    'fuel_provider:add': true,
    'fuel_provider:remove': true,
    'consumable:add': () => !isMobileApp(),
    'consumable:edit': true,
    'admin:add': true,
    'admin:remove': true,
    'manager:add': true,
    'maintenance_manager:add': true,
    'counter_offset:edit': true,
    'EQUIPO_PESADO:add': true,
    'CAMION:add': true,
    'ACTIVO_FIJO:add': true,
    'MOVILIDAD:add': true,
    'project:edit': true,
    [__INHERITS__]: RoleNames.Admin,
  },
  [RoleNames.Root]: {
    'rental_params:company:edit': true,
    'EQUIPO_PESADO:edit': true,
    'CAMION:edit': true,
    'ACTIVO_FIJO:edit': true,
    'MOVILIDAD:edit': true,
    'EQUIPO_PESADO:archive': true,
    'CAMION:archive': true,
    'ACTIVO_FIJO:archive': true,
    'MOVILIDAD:archive': true,
    'EQUIPO_PESADO:unarchive': true,
    'CAMION:unarchive': true,
    'ACTIVO_FIJO:unarchive': true,
    'MOVILIDAD:unarchive': true,
    'EQUIPO_PESADO:delete': true,
    'CAMION:delete': true,
    'ACTIVO_FIJO:delete': true,
    'MOVILIDAD:delete': true,
    'machine_notice:tag:manage': true,
    'work_order:tag:manage': true,
    'maintenance_plan:tag:manage': true,
    'document:tag:manage': true,
    'fuel_flowmeter:edit': true,
    'fuel_dispatch_report:delete': true,
    'fuel_stock_report:delete': true,
    'wo_hours:delete_planned': true,
    'maintenance_plan:remove': true,
    'document:delete': true,
    'manager:add': true,
    'manager:remove': true,
    'rental_approver:add': true,
    'rental_approver:remove': true,
    'rental_external_approver:add': true,
    'rental_external_approver:remove': true,
    'rental_issuer:add': true,
    'rental_issuer:remove': true,
    'project:add': true,
    'project:archive': true,
    'project:unarchive': true,
    'company:edit': true,
    'sidebar:Compañía:Setup': true,
    'sidebar:Compañía:Unidades de negocio': true,
    'sidebar:Compañía:Proyectos': true,
    'sidebar:Compañía:Centros de Costo': true,
    'sidebar:Compañía:Archivo': true,
    'sidebar:Compañía:Equipos': true,
    'rental-certification:force-action': true,
    [__INHERITS__]: [RoleNames.Manager, RoleNames.RentalIssuer],
  },
  [RoleNames.SuperRoot]: {
    [`${BILDERIT_MACHINE_GROUP}:add` as const]: true,
    [`${BILDERIT_MACHINE_GROUP}:edit` as const]: true,
    'rental_params:bilderit:edit': true,
    'maintenance_plan:library:add': true,
    'maintenance_plan:library:remove': true,
    'maintenance_plan:library:edit': true,
    'maintenance_plan:library:tag:manage': true,
    'maintenance_plan:library:publish': true,
    'maintenance_plan:library:unpublish': true,
    'employee:migrate_to_unmanaged': true,
    'machine:BC_Device_id:view': true,
    'sidebar:Bilderit': true,
    'sidebar:Bilderit Handlers SR': true,
    'sidebar:Compañía:Usuarios': true, // superroot si puede gestionar usuarios en la demo
    'debug:view': true,
    // Conectar un asistente de IA al MCP de Bilderit: por ahora sólo super root.
    'mcp:connect': true,
    [__INHERITS__]: [RoleNames.Root, RoleNames.Btrack],
  },
};
